import React, { useMemo, useState } from "react";
import { BookmarkPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ConfirmModal } from "@/components/Common/ConfirmModal/ConfirmModal";
import { useToast } from "@/components/ui/use-toast";
import { useSavedDeploymentConfigStore } from "@/store/savedDeploymentConfig";
import {
  SavedDeploymentConfig,
  SavedDeploymentConfigKind,
  SavedDeploymentConfigPayload
} from "@/types/savedDeploymentConfig";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";

export type SaveConfigDialogProps = {
  kind: SavedDeploymentConfigKind;
  payload: SavedDeploymentConfigPayload;
  selectedConfig?: SavedDeploymentConfig | null;
  isOpen: boolean;
  mode: "new" | "update";
  onOpenChange: (open: boolean) => void;
  onSaved?: (config: SavedDeploymentConfig) => void;
};

/** Name dialog for saving / updating a deployment configuration (no trigger UI). */
export const SaveConfigDialog: React.FC<SaveConfigDialogProps> = ({
  kind,
  payload,
  selectedConfig,
  isOpen,
  mode,
  onOpenChange,
  onSaved
}) => {
  const { toast } = useToast();
  const create = useSavedDeploymentConfigStore((s) => s.create);
  const findByName = useSavedDeploymentConfigStore((s) => s.findByName);

  const [name, setName] = useState("");
  const [overwriteTarget, setOverwriteTarget] = useState<SavedDeploymentConfig | null>(null);

  const kindLabel = kind === "container" ? "container" : "VM";

  // Sync draft name when dialog opens
  React.useEffect(() => {
    if (!isOpen) return;
    setName(mode === "update" ? selectedConfig?.name ?? "" : "");
  }, [isOpen, mode, selectedConfig?.name]);

  const persist = (args: {
    name: string;
    overwriteId?: string;
    isUpdate: boolean;
  }) => {
    const config = create({
      name: args.name,
      kind,
      payload,
      overwriteId: args.overwriteId
    });
    trackEvent(
      args.isUpdate
        ? AnalyticsEventType.SAVED_DEPLOYMENT_CONFIG_UPDATED
        : AnalyticsEventType.SAVED_DEPLOYMENT_CONFIG_SAVED,
      {
        kind,
        schemaVersion: config.schemaVersion
      }
    );
    toast({
      title: args.isUpdate ? "Configuration updated" : "Configuration saved",
      description: config.name
    });
    onSaved?.(config);
    onOpenChange(false);
    setOverwriteTarget(null);
  };

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    if (mode === "update" && selectedConfig) {
      persist({
        name: trimmed,
        overwriteId: selectedConfig.id,
        isUpdate: true
      });
      return;
    }

    const clash = findByName(kind, trimmed);
    if (clash) {
      setOverwriteTarget(clash);
      return;
    }

    persist({ name: trimmed, isUpdate: false });
  };

  const dialogTitle = useMemo(() => {
    if (mode === "update") return "Update configuration";
    return "Save configuration";
  }, [mode]);

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onOpenChange(false);
        }}
      >
        <DialogContent className="w-full gap-0 px-0 py-0" showCloseIcon={false}>
          <DialogHeader className="px-8 pt-4">
            <DialogTitle className="title-h4 text-foreground">{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 px-8 py-4">
            <p className="subtext-small text-muted-foreground">
              Save this {kindLabel} setup to reuse when creating future deployments. Stored in this
              browser only and removed when you sign out. Account sync coming soon. Fields marked
              with a bookmark are included.
            </p>
            <label className="label-small text-foreground" htmlFor="saved-config-name">
              Name <span className="text-blue-light-300">*</span>
            </label>
            <Input
              id="saved-config-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My H100 vLLM"
              maxLength={255}
              autoFocus
            />
          </div>
          <DialogFooter className="flex flex-row !justify-center gap-2 rounded-b-lg border-t px-4 py-4">
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={!name.trim()} onClick={handleSubmit}>
              {mode === "update" ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        isOpen={Boolean(overwriteTarget)}
        title="Overwrite configuration?"
        description={
          overwriteTarget
            ? `A ${kindLabel} configuration named “${overwriteTarget.name}” already exists. Replace it with the current settings?`
            : null
        }
        submitLabel="Overwrite"
        onClose={() => setOverwriteTarget(null)}
        onSubmit={() => {
          if (!overwriteTarget) return;
          persist({
            name: overwriteTarget.name,
            overwriteId: overwriteTarget.id,
            isUpdate: true
          });
        }}
      />
    </>
  );
};

/** @deprecated Prefer SaveConfigDialog + SavedConfigsSelect save actions */
export const SaveConfigButton: React.FC<{
  kind: SavedDeploymentConfigKind;
  payload: SavedDeploymentConfigPayload;
  selectedConfig?: SavedDeploymentConfig | null;
  disabled?: boolean;
  onSaved?: (config: SavedDeploymentConfig) => void;
}> = ({ kind, payload, selectedConfig, disabled, onSaved }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"new" | "update">("new");

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={disabled}
          onClick={() => {
            setMode("new");
            setIsOpen(true);
          }}
        >
          <BookmarkPlus className="h-3.5 w-3.5" />
          {selectedConfig ? "Save as new" : "Save configuration"}
        </Button>
        {selectedConfig && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => {
              setMode("update");
              setIsOpen(true);
            }}
          >
            Update “{selectedConfig.name}”
          </Button>
        )}
      </div>
      <SaveConfigDialog
        kind={kind}
        payload={payload}
        selectedConfig={selectedConfig}
        isOpen={isOpen}
        mode={mode}
        onOpenChange={setIsOpen}
        onSaved={onSaved}
      />
    </>
  );
};
